import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from '../hooks/useSocket';
import { toast } from 'sonner';

const NotificationContext = createContext(null);

// Check if browser supports notifications
const isNotificationSupported = () => {
  return 'Notification' in window;
};

// Request notification permission
const requestPermission = async () => {
  if (!isNotificationSupported()) return 'denied';
  
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }
  
  return Notification.permission;
};

// Show browser push notification
const showPushNotification = (title, options = {}) => {
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    return null;
  }
  
  const notification = new Notification(title, {
    icon: '/logo192.png',
    badge: '/logo192.png',
    requireInteraction: false,
    silent: false,
    ...options,
  });
  
  // Auto close after 5 seconds
  setTimeout(() => notification.close(), 5000);
  
  return notification;
};

export function NotificationProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const { subscribe, isAuthenticated: isSocketAuth } = useSocket();
  const [permission, setPermission] = useState(
    isNotificationSupported() ? Notification.permission : 'denied'
  );
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [preferences, setPreferences] = useState(() => {
    // Initialize preferences from localStorage
    const saved = localStorage.getItem('notificationPreferences');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to load notification preferences');
      }
    }
    return {
      pushEnabled: true,
      soundEnabled: true,
      chatMessages: true,
      caseUpdates: true,
      taskReminders: true,
      systemAlerts: true,
    };
  });

  // Request permission on mount
  useEffect(() => {
    if (isAuthenticated && isNotificationSupported()) {
      requestPermission().then(setPermission);
    }
  }, [isAuthenticated]);

  // Save preferences to localStorage
  const updatePreferences = useCallback((newPrefs) => {
    const updated = { ...preferences, ...newPrefs };
    setPreferences(updated);
    localStorage.setItem('notificationPreferences', JSON.stringify(updated));
  }, [preferences]);

  // Handle incoming notification
  const handleNotification = useCallback((notification) => {
    // Add to local state
    setNotifications(prev => [notification, ...prev].slice(0, 50));
    setUnreadCount(prev => prev + 1);

    // Show toast notification
    toast.info(notification.title, {
      description: notification.message,
    });

    // Show push notification if enabled
    if (preferences.pushEnabled && permission === 'granted') {
      // Check notification type preferences
      const shouldShow = (
        (notification.type === 'chat' && preferences.chatMessages) ||
        (notification.type === 'case' && preferences.caseUpdates) ||
        (notification.type === 'task' && preferences.taskReminders) ||
        (notification.type === 'system' && preferences.systemAlerts) ||
        !notification.type
      );

      if (shouldShow) {
        const pushNotif = showPushNotification(notification.title, {
          body: notification.message,
          tag: notification.id || Date.now().toString(),
          data: notification.data,
        });

        // Handle notification click
        if (pushNotif) {
          pushNotif.onclick = () => {
            window.focus();
            if (notification.link) {
              window.location.href = notification.link;
            }
            pushNotif.close();
          };
        }
      }
    }

    // Play sound if enabled
    if (preferences.soundEnabled) {
      try {
        const audio = new Audio('/notification.mp3');
        audio.volume = 0.3;
        audio.play().catch(() => {});
      } catch (e) {
        // Sound not available
      }
    }
  }, [preferences, permission]);

  // Subscribe to WebSocket notifications
  useEffect(() => {
    if (!isSocketAuth) return;

    const unsubNew = subscribe('notification:new', handleNotification);
    
    const unsubChat = subscribe('chat:new_message', (data) => {
      if (preferences.chatMessages) {
        handleNotification({
          id: data.message?.message_id,
          type: 'chat',
          title: `New message from ${data.message?.sender?.name || 'Someone'}`,
          message: data.message?.content?.slice(0, 100) || 'New message',
          link: `/team-chat?chat=${data.chat_id}`,
          data: { chat_id: data.chat_id, message_id: data.message?.message_id },
        });
      }
    });

    return () => {
      unsubNew();
      unsubChat();
    };
  }, [isSocketAuth, subscribe, handleNotification, preferences.chatMessages]);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  // Clear notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Request permission manually
  const requestNotificationPermission = useCallback(async () => {
    const perm = await requestPermission();
    setPermission(perm);
    return perm;
  }, []);

  const value = {
    permission,
    isSupported: isNotificationSupported(),
    unreadCount,
    notifications,
    preferences,
    updatePreferences,
    markAllAsRead,
    clearNotifications,
    requestPermission: requestNotificationPermission,
    showPushNotification: (title, options) => {
      if (preferences.pushEnabled) {
        return showPushNotification(title, options);
      }
      return null;
    },
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationContext;
